import creditRatios from '@klv/shared/pricing/static/credit-ratio-v2.json';
import carrierFees from '@klv/shared/pricing/static/carrier-fees.json';
import { calculateRequiredCreditTier, calculatePricePerSend, calculateMaxSends, getPriceForCreditTier } from '../../utils/pricing';

const countriesData = creditRatios.countries;
const smsCarrierFees = carrierFees.sms;
const mmsCarrierFees = carrierFees.mms;

export type TCreditType = 'sms' | 'mms' | 'whatsapp-marketing' | 'whatsapp-transactional';
export type TSmsCarrierFees = keyof typeof carrierFees['sms'];
export type TMmsCarrierFees = keyof typeof carrierFees['mms'];

export const getAllCountries = (): string[] => {
  return Object.keys(countriesData);
};

export const calculateEffectiveCredits = (
  messages: number,
  selectedLocale: string,
  creditType: TCreditType
): number => {
  if (!messages) return 0;
  
  return calculateRequiredCreditTier(messages, selectedLocale, creditType);
};

export const calculatePricingInfo = (
  effectiveCredits: number,
  selectedLocale: string,
  creditType: TCreditType
) => {
  
  return {
    price: getPriceForCreditTier(effectiveCredits),
    maxSends: calculateMaxSends(effectiveCredits, selectedLocale, creditType),
    pricePerSend: calculatePricePerSend(effectiveCredits, selectedLocale, creditType)
  };
};

export const getSelectedFee = (
  creditType: TCreditType,
  selectedLocale: string
): number => {
  return creditType === 'mms'
    ? mmsCarrierFees[selectedLocale as TMmsCarrierFees]
    : smsCarrierFees[selectedLocale as TSmsCarrierFees];
};

export const isSmsAvailable = (selectedLocale: string): boolean => {
  return countriesData[selectedLocale as keyof typeof countriesData]?.sms > 0;
};

export const isMmsAvailable = (selectedLocale: string): boolean => {
  return countriesData[selectedLocale as keyof typeof countriesData]?.mms > 0;
};

export const getOptionRatio = (creditType: TCreditType, code: string): number => {
  try {
    return countriesData[code as keyof typeof countriesData]?.[creditType] || 0;
  } catch {
    return 0;
  }
};

/**
 * Check if a credit type is available for a given locale
 * Uses the consistent pattern: 0 = not available, > 0 = available
 */
export const isCreditTypeAvailable = (creditType: TCreditType, selectedLocale: string): boolean => {
  const ratio = getOptionRatio(creditType, selectedLocale);
  return ratio !== undefined && ratio > 0;
};

export const hasCarrierFees = (
  creditType: TCreditType,
  selectedLocale: string
): boolean => {
  const carrierFeesForLocale = creditType === 'mms'
    ? mmsCarrierFees[selectedLocale as TMmsCarrierFees]
    : smsCarrierFees[selectedLocale as TSmsCarrierFees];
  
  return !!carrierFeesForLocale;
};
